/**
 * Day-scoped GPS playback scrubber (Truth Layer TimeSlider pattern without date picker).
 * Default `floating` = compact translucent bar for map overlay.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { STATUS_COLORS, CHART_PALETTE } from '../../config/colors';
import {
  formatUkPlaybackClock,
  formatUkPlaybackDate,
  type PlaybackEventMarker,
} from '../../utils/vehicleDayPlaybackTrack';

/**
 * Track points between observed stops are spaced evenly in time along the
 * route segment (see vehicleDayPlaybackTrack.interpolateSegmentPoints) — the
 * route is real, the in-between timing is not. Kept in tooltips only so the
 * chrome does not read as “don’t trust this map.”
 */
const REPLAY_TIMING_NOTE =
  'Stop times are from telematics. The clock between stops is spaced evenly along the route (not timed GPS ticks).';

const ROUTE_SOURCE_LABEL = 'Route: telematics';

const SPEED_OPTIONS = [
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 5, label: '5x' },
  { value: 10, label: '10x' },
];

/** How far ahead of the playhead an event still counts as "near". */
const NEAR_EVENT_TOLERANCE_MS = 30_000;

const PLAYBACK_TICK_MS = 100;

export interface GpsPlaybackScrubberProps {
  fromIso: string;
  toIso: string;
  currentTimestamp: string | null;
  isPlaying: boolean;
  playbackSpeed: number;
  events?: PlaybackEventMarker[];
  onPlaybackControlsChange: (
    playing: boolean,
    speed: number,
    timestamp: string | null,
  ) => void;
  disabled?: boolean;
}

function eventColor(kind: PlaybackEventMarker['kind']): string {
  if (kind === 'start') return STATUS_COLORS.success;
  if (kind === 'end') return STATUS_COLORS.destructive;
  if (kind === 'delivery') return CHART_PALETTE[1];
  if (kind === 'handoff') return CHART_PALETTE[9];
  return CHART_PALETTE[7];
}

export default function GpsPlaybackScrubber({
  fromIso,
  toIso,
  currentTimestamp,
  isPlaying,
  playbackSpeed,
  events = [],
  onPlaybackControlsChange,
  disabled = false,
}: GpsPlaybackScrubberProps) {
  const [sliderValue, setSliderValue] = useState(0);
  const playbackInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Seed playhead only when play starts / speed changes — not every parent tick. */
  const timestampRef = useRef<string | null>(currentTimestamp);
  const onChangeRef = useRef(onPlaybackControlsChange);
  timestampRef.current = currentTimestamp;
  onChangeRef.current = onPlaybackControlsChange;

  const activeEvent = useMemo(() => {
    if (!currentTimestamp || events.length === 0) return null;
    const t = Date.parse(currentTimestamp);
    if (!Number.isFinite(t)) return null;
    let best: PlaybackEventMarker | null = null;
    for (const e of events) {
      if (e.t <= t + NEAR_EVENT_TOLERANCE_MS) best = e;
      else break;
    }
    return best;
  }, [currentTimestamp, events]);

  /** True when the playhead is not sitting on a stop/event mark. */
  const betweenStops = useMemo(() => {
    if (!currentTimestamp || events.length === 0) return true;
    const t = Date.parse(currentTimestamp);
    if (!Number.isFinite(t)) return true;
    return !events.some((e) => Math.abs(e.t - t) <= NEAR_EVENT_TOLERANCE_MS);
  }, [currentTimestamp, events]);

  useEffect(() => {
    if (!isPlaying || disabled) {
      if (playbackInterval.current) {
        clearInterval(playbackInterval.current);
        playbackInterval.current = null;
      }
      return;
    }

    const fromTime = Date.parse(fromIso);
    const toTime = Date.parse(toIso);
    const duration = Math.max(1, toTime - fromTime);
    let currentTime = timestampRef.current ? Date.parse(timestampRef.current) : fromTime;
    if (!Number.isFinite(currentTime) || currentTime >= toTime) currentTime = fromTime;

    const timeAdvance = (duration / 100) * (playbackSpeed / 10);

    playbackInterval.current = setInterval(() => {
      currentTime += timeAdvance;
      if (currentTime >= toTime) {
        onChangeRef.current(false, playbackSpeed, new Date(toTime).toISOString());
        setSliderValue(100);
        if (playbackInterval.current) {
          clearInterval(playbackInterval.current);
          playbackInterval.current = null;
        }
      } else {
        const progress = ((currentTime - fromTime) / duration) * 100;
        setSliderValue(progress);
        onChangeRef.current(true, playbackSpeed, new Date(currentTime).toISOString());
      }
    }, PLAYBACK_TICK_MS);

    return () => {
      if (playbackInterval.current) {
        clearInterval(playbackInterval.current);
        playbackInterval.current = null;
      }
    };
    // Intentionally omit currentTimestamp — including it restarts the interval every tick.
  }, [isPlaying, playbackSpeed, fromIso, toIso, disabled]);

  useEffect(() => {
    if (!currentTimestamp) {
      setSliderValue(0);
      return;
    }
    const fromTime = Date.parse(fromIso);
    const toTime = Date.parse(toIso);
    const currentTime = Date.parse(currentTimestamp);
    const duration = Math.max(1, toTime - fromTime);
    setSliderValue(Math.max(0, Math.min(100, ((currentTime - fromTime) / duration) * 100)));
  }, [currentTimestamp, fromIso, toIso]);

  const eventMarks = useMemo(() => {
    const fromTime = Date.parse(fromIso);
    const toTime = Date.parse(toIso);
    const duration = Math.max(1, toTime - fromTime);
    return events.map((e) => ({
      ...e,
      pct: Math.max(0, Math.min(100, ((e.t - fromTime) / duration) * 100)),
    }));
  }, [events, fromIso, toIso]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setSliderValue(value);
    const fromTime = Date.parse(fromIso);
    const toTime = Date.parse(toIso);
    const duration = Math.max(1, toTime - fromTime);
    const newTime = fromTime + (duration * value) / 100;
    onPlaybackControlsChange(false, playbackSpeed, new Date(newTime).toISOString());
  };

  const clockTitle = betweenStops
    ? `UK local time (Europe/London). ${REPLAY_TIMING_NOTE}`
    : 'UK local time (Europe/London). Stop time from telematics.';

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-border/70 bg-popover/85 backdrop-blur-sm shadow-lg gps-playback-scrubber"
      title={activeEvent?.label ?? REPLAY_TIMING_NOTE}
    >
      <div className="flex flex-col leading-tight shrink-0 pl-0.5" title={REPLAY_TIMING_NOTE}>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Replay
        </span>
        <span className="text-xs font-medium text-muted-foreground normal-case tracking-normal">
          {ROUTE_SOURCE_LABEL}
        </span>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() =>
          onPlaybackControlsChange(!isPlaying, playbackSpeed, currentTimestamp)
        }
        className="p-1 bg-primary disabled:opacity-40 rounded-md transition-colors shrink-0"
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <Pause className="w-3.5 h-3.5 text-white" />
        ) : (
          <Play className="w-3.5 h-3.5 text-white" />
        )}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          onPlaybackControlsChange(false, playbackSpeed, null);
          setSliderValue(0);
        }}
        className="p-1 bg-secondary hover:bg-accent disabled:opacity-40 rounded-md transition-colors shrink-0"
        title="Reset to start"
      >
        <RotateCcw className="w-3.5 h-3.5 text-foreground" />
      </button>
      <div className="flex items-center gap-0.5 shrink-0">
        {SPEED_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() =>
              onPlaybackControlsChange(isPlaying, option.value, currentTimestamp)
            }
            className={`px-1.5 py-0.5 text-xs font-medium rounded transition-all disabled:opacity-40 ${
              playbackSpeed === option.value
                ? 'bg-primary text-white'
                : 'bg-secondary text-foreground hover:bg-accent'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <span
        className="text-sm font-semibold text-foreground tabular-nums shrink-0 text-center whitespace-nowrap"
        title={clockTitle}
      >
        <span className="text-xs font-medium text-foreground">{formatUkPlaybackDate(currentTimestamp ?? fromIso)}</span>
        <span className="ml-1.5">{formatUkPlaybackClock(currentTimestamp ?? fromIso)}</span>
        <span className="ml-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">UK</span>
      </span>
      <div className="relative flex-1 min-w-40 pt-2.5">
        <div className="absolute left-0 right-0 top-0 h-2.5 pointer-events-none">
          {eventMarks.map((m) => (
            <span
              key={m.id}
              title={m.label}
              className="absolute top-0 w-0.5 h-2.5 rounded-sm"
              style={{ left: `calc(${m.pct}% - 1px)`, backgroundColor: eventColor(m.kind) }}
            />
          ))}
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={sliderValue}
          disabled={disabled}
          onChange={handleSliderChange}
          className="w-full h-1.5 bg-secondary rounded-lg appearance-none cursor-pointer gps-scrubber-thumb disabled:opacity-40"
          style={{
            background: `linear-gradient(to right, ${STATUS_COLORS.success} 0%, ${STATUS_COLORS.success} ${sliderValue}%, rgb(var(--secondary)) ${sliderValue}%, rgb(var(--secondary)) 100%)`,
          }}
          aria-label="GPS replay scrubber"
        />
      </div>
      {activeEvent ? (
        <span className="text-xs text-muted-foreground truncate max-w-32 shrink-0 hidden xl:inline">
          {activeEvent.label}
        </span>
      ) : null}
    </div>
  );
}
